/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { loadDocs, deleteDocs } from './documents-middleware';
import { logout } from '../../auth/auth-middleware';
import { AppDocument } from 'api';

type DeleteDocs = {
    docsCompletedWithError: { id: string }[];
    deletedDocsIds: { id: string }[];
    isLoading: boolean;
    hasErrors: boolean;
};

type DocumentsState = {
    list: AppDocument[];
    error: string | null;
    isLoading: boolean;
    deletedDocs: DeleteDocs;
};

const initialState: DocumentsState = {
    list: [],
    error: null,
    isLoading: false,
    deletedDocs: {
        docsCompletedWithError: [],
        deletedDocsIds: [],
        isLoading: false,
        hasErrors: false,
    },
};

export const documentsSlice = createSlice({
    name: 'app/documents',
    initialState,
    reducers: {
        reset: (state) => {
            state.list = [];
            state.error = null;
            state.isLoading = false;
            state.deletedDocs = {
                docsCompletedWithError: [],
                deletedDocsIds: [],
                isLoading: false,
                hasErrors: false,
            };
        },
        resetDeleted: (state) => {
            state.deletedDocs = {
                docsCompletedWithError: [],
                deletedDocsIds: [],
                isLoading: false,
                hasErrors: false,
            };
        },
        // New case to update a document in the list
        updateDocInList: (state, action: PayloadAction<AppDocument>) => {
            const updatedDocument = action.payload;
            state.list = state.list.map((doc) =>
                doc.id === updatedDocument.id ? updatedDocument : doc
            );
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(loadDocs.fulfilled, (state, action) => {
                state.list = [...action.payload.data].sort((a, b) =>
                    a.document_name.localeCompare(b.document_name)
                );
                state.error = null;
                state.isLoading = false;
            })
            .addCase(loadDocs.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })

            .addCase(loadDocs.rejected, (state, action) => {
                state.error = action.payload as string;
                state.isLoading = false;
            })
            .addCase(deleteDocs.fulfilled, (state, action) => {
                state.deletedDocs.isLoading = false;
                const results = action.payload
                    .filter((val): val is PromiseFulfilledResult<any> => val.status === 'fulfilled')
                    .map((val) => val.value);
                const removed = results.filter((item) => !item.error);
                if (removed.length > 0) {
                    const removedDocsIds = removed.map((item) => ({ id: item.meta.arg }));
                    state.deletedDocs.deletedDocsIds = removedDocsIds;
                    state.deletedDocs.hasErrors = false;
                }
                const completedWithError = results.filter((item) => item.error);
                if (completedWithError.length > 0) {
                    const docsWithError = completedWithError.map((item) => ({ id: item.meta.arg }));
                    state.deletedDocs.hasErrors = true;
                    state.deletedDocs.docsCompletedWithError = docsWithError;
                    return;
                }
                state.deletedDocs.hasErrors = false;
                state.deletedDocs.docsCompletedWithError = [];
            })
            .addCase(deleteDocs.pending, (state) => {
                state.deletedDocs.isLoading = true;
            })
            .addCase(logout.fulfilled, (state) => {
                state.list = [];
                state.error = null;
                state.isLoading = false;
                state.deletedDocs = {
                    docsCompletedWithError: [],
                    deletedDocsIds: [],
                    isLoading: false,
                    hasErrors: false,
                };
            });
    },
});

export const { reset, resetDeleted, updateDocInList } = documentsSlice.actions;
export default documentsSlice.reducer;
